/**
 * Shapes mirrored from backend `review` admin module.
 */
export type AdminReview = {
  id: string;
  rating: number;
  comment: string | null;
  reviewer_name: string;
  vendor_profile_id: string;
  vendor_business_name: string | null;
  is_hidden: boolean;
  reported_at: string | null;
  report_reason: string | null;
  created_at: string;
};

export type AdminReviewListResponse = {
  items: AdminReview[];
  total: number;
  page: number;
  page_size: number;
};

export type AdminReviewFilters = {
  page?: number;
  page_size?: number;
  reported?: boolean;
  hidden?: boolean;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** Narrow to one vendor. */
  vendor_profile_id?: string;
  /** Free text across the comment, the reviewer and the vendor. */
  q?: string;
  /** Exactly this many stars. */
  rating?: number | string;
  /** At most this many stars — how you find the 1-star nobody reported. */
  rating_max?: number | string;
};

export const REVIEW_SORT_FIELDS = ['created_at', 'rating', 'reported_at'] as const;

/** What the tiles above the moderation list count. */
export type ReviewCounts = {
  total: number;
  reported: number;
  /**
   * Reported AND still visible.
   *
   * The only number on this screen that is a job. Reported-and-hidden is finished work;
   * reported-and-visible is a complaint sitting on a vendor's page that nobody has looked
   * at, and the old screen could not tell the two apart.
   */
  unactioned: number;
  hidden: number;
  /** Visible 1- and 2-star reviews — costing a vendor business whether or not anyone reported them. */
  low_rated: number;
  average_rating: number | null;
};

/** A rating as stars, for a column that has to be scannable down the page. */
export function stars(rating: number): string {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export function rating_tone(rating: number): 'destructive' | 'warning' | 'success' {
  if (rating <= 2) return 'destructive';
  if (rating === 3) return 'warning';
  return 'success';
}

/** Whether this review is a job rather than a record. */
export function needs_action(r: AdminReview): boolean {
  return r.reported_at !== null && !r.is_hidden;
}
